'use client';

import type { HTMLAttributes } from 'react';
import { createElement, useEffect, useState } from 'react';
import { cx } from '../utils/cx';

import type { IconName, ResolvedIcon } from '../../packages/icons/icon-registry';
import { IconRegistry } from '../../packages/icons/icon-registry';

export type IconAs = 'span' | 'div';

export interface IconProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  name?: IconName | string | null;
  icon?: IconName | string | null;
  as?: IconAs;
  size?: number;
  strokeWidth?: number;
  label?: string;
  decorative?: boolean;
  className?: string;
}

const warnedMissingIcons = new Set<string>();

function normalizeAriaHiddenProp<T extends Record<string, unknown>>(props: T): T {
  if (!Object.prototype.hasOwnProperty.call(props, 'ariaHidden')) {
    return props;
  }

  const next = { ...props } as Record<string, unknown>;
  const ariaHidden = next.ariaHidden;

  delete next.ariaHidden;

  if (!Object.prototype.hasOwnProperty.call(next, 'aria-hidden')) {
    next['aria-hidden'] = ariaHidden;
  }

  return next as T;
}

export function Icon({
  name,
  icon,
  as = 'span',
  size = 16,
  strokeWidth = 2,
  label,
  decorative = false,
  className,
  ...rest
}: IconProps) {
  const resolvedName = (name ?? icon ?? null) as IconName | string | null;

  // The registry loads icon libraries asynchronously and provides its own
  // synchronous fallbacks for common names. Trigger loading once and re-render
  // when ready so icons without a synchronous fallback (e.g. "send") appear.
  const [ready, setReady] = useState<boolean>(() => IconRegistry.isReady());

  useEffect(() => {
    if (ready) return;

    let active = true;
    IconRegistry.init().then(() => {
      if (active) setReady(true);
    });

    return () => {
      active = false;
    };
  }, [ready]);

  const IconComponent: ResolvedIcon | null = resolvedName
    ? IconRegistry.get(String(resolvedName))
    : null;

  if (
    process.env.NODE_ENV !== 'production' &&
    typeof window !== 'undefined' &&
    resolvedName &&
    !IconComponent &&
    IconRegistry.isReady()
  ) {
    const key = String(resolvedName);
    if (!warnedMissingIcons.has(key)) {
      warnedMissingIcons.add(key);
      // eslint-disable-next-line no-console
      console.warn(`[Icon] Icon "${key}" was requested but not found in IconRegistry.`);
    }
  }

  const computedLabel = String(label ?? '').trim();
  const isDecorative = decorative || !computedLabel;

  const a11yProps = isDecorative
    ? ({ 'aria-hidden': true, role: 'presentation' } as const)
    : ({ role: 'img', 'aria-label': computedLabel } as const);

  const wrapperClassName = cx('inline-flex items-center justify-center leading-none', className);
  const Component: IconAs = as;
  const normalizedRest = normalizeAriaHiddenProp(rest as Record<string, unknown>);

  return createElement(
    Component,
    { ...normalizedRest, ...a11yProps, className: wrapperClassName },
    IconComponent ? (
      <IconComponent size={size} strokeWidth={strokeWidth} aria-hidden="true" focusable="false" className="shrink-0" />
    ) : null,
  );
}

Icon.displayName = 'Icon';